/**
 * Confirmation venue d'une autre page (mouvement enregistré, commande créée,
 * fournisseur archivé) : dite en tête de la page, puis retirée de l'historique
 * pour ne pas réapparaître au retour arrière ni au rechargement. Une seule
 * lecture de `location.state.feedback` pour tout l'écran :
 *  - `ReturnedFeedback` quand le bandeau suffit ;
 *  - `useReturnedFeedback` quand la page place le message elle-même ;
 *  - `useArrivedFeedback` quand la page garde un message local, qu'un geste
 *    de la page remplace (« Facture émise »).
 * Fermer le bandeau garde le reste de l'état de navigation (`from`, retour
 * vers la liste d'origine) et l'adresse complète, requête et ancre comprises.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Banner } from './Feedback.js';

const FEEDBACK_KEYS = ['feedback', 'feedbackTo', 'feedbackState', 'warning', 'queued'];

export function useReturnedFeedback(): { feedback: string | undefined; dismiss: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? null) as Record<string, unknown> | null;
  const feedback = typeof state?.feedback === 'string' && state.feedback ? state.feedback : undefined;
  return {
    feedback,
    dismiss: () => {
      const rest = Object.fromEntries(Object.entries(state ?? {}).filter(([key]) => !FEEDBACK_KEYS.includes(key)));
      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: Object.keys(rest).length ? rest : null,
      });
    },
  };
}

/** Message local initialisé par la page précédente, puis retiré de l'historique dès l'arrivée. */
export function useArrivedFeedback(): [string | undefined, (message: string | undefined) => void] {
  const { feedback: arrived, dismiss } = useReturnedFeedback();
  const [feedback, setFeedback] = useState<string | undefined>(arrived);
  const cleared = useRef(false);
  useEffect(() => {
    if (arrived && !cleared.current) {
      cleared.current = true;
      dismiss();
    }
  }, [arrived, dismiss]);
  return [feedback, setFeedback];
}

export function ReturnedFeedback(): ReactNode {
  const { feedback, dismiss } = useReturnedFeedback();
  return feedback ? <Banner onDismiss={dismiss}>{feedback}</Banner> : null;
}
